import { Gauge, CalendarClock, AlarmClock, ShieldAlert, AlertTriangle, MessageSquareText, Layers } from "lucide-react";
import { Link } from "react-router-dom";
import { SectionCard } from "@/components/common/SectionCard";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { launchBadge, isOverdue, WORKSTREAM_COLOR } from "@/data/launchOptions";
import { LaunchCountdown } from "@/components/launch/LaunchCountdown";

const fmt = (iso) => { try { return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

const List = ({ items, empty, render, testid }) => (
  items.length === 0
    ? <EmptyState icon={empty.icon} title={empty.title} description={empty.desc} />
    : <ul className="space-y-3" data-testid={testid}>{items.map(render)}</ul>
);

const WORKSTREAM_ORDER = ["Product", "Content", "Commercial", "Marketing", "Validation", "Launch"];

export const LaunchOverview = ({ milestones, updates, readiness, workstreamReadiness = {}, today, onOpen, refreshKey }) => {
  const upcoming = [...milestones].filter((m) => m.status !== "Complete" && m.due_date && m.due_date >= today).sort((a, b) => a.due_date.localeCompare(b.due_date)).slice(0, 6);
  const overdue = milestones.filter((m) => isOverdue(m, today));
  const blocked = milestones.filter((m) => m.status === "Blocked");
  const critical = milestones.filter((m) => m.priority === "Critical" && m.status !== "Complete");
  const recent = [...updates].slice(0, 6);
  const complete = milestones.filter((m) => m.status === "Complete").length;

  const line = (m) => (
    <li key={m.id}>
      <button onClick={() => onOpen(m)} aria-label={`View milestone: ${m.title}`} data-testid={`overview-milestone-${m.id}`} className="flex w-full items-start justify-between gap-3 border-b border-[#EEF2FB] pb-3 text-left last:border-0 last:pb-0 hover:text-[#E0A82E] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E0A82E]/40">
        <div className="min-w-0"><p className="truncate text-sm font-semibold text-[#14245F]">{m.title}</p><p className="text-xs text-[#5A6B8C]">{m.owner}{m.due_date ? ` · ${m.due_date}` : ""}</p></div>
        <StatusBadge status={launchBadge(m.status)} label={m.status} />
      </button>
    </li>
  );

  return (
    <div className="space-y-6">
      <LaunchCountdown variant="tracker" readiness={readiness} refreshKey={refreshKey} />

      {/* Readiness hero + phase journey */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <SectionCard testid="readiness-card" accent="#E0A82E" eyebrow="Expedition Progress" icon={Gauge} title="Launch Readiness" className="lg:col-span-1">
          {milestones.length === 0 ? (
            <EmptyState icon={Gauge} title="0% ready" description="Add launch milestones to begin tracking readiness." />
          ) : (
            <Link
              to="/launch?view=table"
              data-testid="readiness-donut-link"
              aria-label="View all active milestones contributing to readiness"
              className="flex flex-col items-center py-2 text-center transition-transform hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E0A82E]/40 rounded-2xl"
            >
              <div className="relative flex h-36 w-36 items-center justify-center rounded-full"
                style={{ background: `conic-gradient(#E0A82E ${readiness * 3.6}deg, #EEF2FB 0deg)` }}>
                <div className="flex h-28 w-28 flex-col items-center justify-center rounded-full bg-white">
                  <span className="font-numeric text-4xl font-bold text-[#14245F]" data-testid="readiness-value">{readiness}%</span>
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-[#5A6B8C]">ready</span>
                </div>
              </div>
              <p className="mt-4 text-sm font-medium text-[#5A6B8C]">
                Weighted readiness · <span className="font-bold text-[#16A34A]">{complete}</span> of {milestones.length} milestones complete
              </p>
              <span className="mt-1 text-xs font-semibold text-[#E0A82E]">View milestones →</span>
            </Link>
          )}
        </SectionCard>

        <SectionCard testid="phases-card" accent="#06B6D4" eyebrow="Weighted Readiness" icon={Layers} title="Readiness by Workstream" className="lg:col-span-2">
          <ul className="space-y-1" data-testid="workstream-readiness-list">
            {WORKSTREAM_ORDER.map((ws) => {
              const pct = workstreamReadiness[ws] ?? 0;
              const color = WORKSTREAM_COLOR[ws] || "#06B6D4";
              return (
                <li key={ws}>
                  <Link
                    to={`/launch?view=table&workstream=${encodeURIComponent(ws)}`}
                    data-testid={`workstream-readiness-${ws}`}
                    aria-label={`View ${ws} milestones (${pct}% ready)`}
                    className="flex items-center gap-3 rounded-xl px-2 py-1.5 transition-colors hover:bg-[#F5F8FF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#06B6D4]/40"
                  >
                    <span className="inline-flex w-28 shrink-0 items-center gap-1.5 text-sm font-semibold text-[#14245F]"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />{ws}</span>
                    <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-[#EEF2FB]">
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
                    </div>
                    <span className="w-12 shrink-0 text-right font-numeric text-sm font-bold" style={{ color }}>{pct}%</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SectionCard testid="upcoming-card" accent="#06B6D4" icon={CalendarClock} title="Upcoming Deadlines"><List testid="upcoming-list" items={upcoming} empty={{ icon: CalendarClock, title: "Nothing upcoming", desc: "Future deadlines appear here." }} render={line} /></SectionCard>
        <SectionCard testid="overdue-card" accent="#DC2626" icon={AlarmClock} title="Overdue Milestones"><List testid="overdue-list" items={overdue} empty={{ icon: AlarmClock, title: "Nothing overdue", desc: "The launch route is on schedule." }} render={line} /></SectionCard>
        <SectionCard testid="blocked-card" accent="#FB6F5A" icon={ShieldAlert} title="Blocked Milestones"><List testid="blocked-list" items={blocked} empty={{ icon: ShieldAlert, title: "No blockers in the path", desc: "Blocked milestones appear here." }} render={line} /></SectionCard>
        <SectionCard testid="critical-card" accent="#E0A82E" icon={AlertTriangle} title="Critical Milestones"><List testid="critical-list" items={critical} empty={{ icon: AlertTriangle, title: "No critical items", desc: "Critical milestones appear here." }} render={line} /></SectionCard>
      </div>

      <SectionCard testid="recent-updates-card" accent="#0EA5A4" icon={MessageSquareText} title="Recent Launch Updates">
        {recent.length === 0 ? (
          <EmptyState icon={MessageSquareText} title="No updates yet" description="Post updates on milestones to build a timeline." />
        ) : (
          <ul className="space-y-3" data-testid="recent-updates-list">
            {recent.map((u) => {
              const rel = u.related_id ? milestones.find((x) => x.id === u.related_id) : null;
              const body = (<>
                <div className="flex items-center justify-between gap-2"><p className="font-semibold text-[#14245F]">{u.title}</p><span className="shrink-0 text-xs text-[#5A6B8C]">{u.author} · {fmt(u.created_at)}</span></div>
                <p className="mt-0.5 text-sm text-[#5A6B8C]">{u.body}</p>
                {u.related_name && <p className="mt-1 text-[11px] text-[#5A6B8C]">↳ {u.related_name}</p>}
              </>);
              return rel ? (
                <li key={u.id}>
                  <button onClick={() => onOpen(rel)} aria-label={`View milestone: ${rel.title}`} data-testid={`recent-update-${u.id}`} className="w-full rounded-2xl border border-[#E4E9F3] p-3 text-left transition-colors hover:border-[#0EA5A4]/50 hover:bg-[#0EA5A4]/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0EA5A4]/40">{body}</button>
                </li>
              ) : (
                <li key={u.id} className="rounded-2xl border border-[#E4E9F3] p-3">{body}</li>
              );
            })}
          </ul>
        )}
      </SectionCard>
    </div>
  );
};
